/** Watchlist 추천 사유 + shadow quant score. */
import { WatchlistAnalysis, WatchlistSymbol } from '../../services/operationsApi';
import OperationsEvidenceSummary from './OperationsEvidenceSummary';

interface Props {
  analysis?: WatchlistAnalysis;
}

const STATUS_LABEL: Record<WatchlistSymbol['status'], string> = {
  watchlist: '후보',
  holding: '보유',
  watchlist_holding: '후보+보유',
};

const FLAG_LABEL: Record<string, string> = {
  recent_loss_45d: '최근 손실 45일',
  no_candidate_rationale: '추천 사유 없음',
  sparse_rationale: '사유 부족',
};

export default function OperationsWatchlist({ analysis }: Props) {
  if (!analysis) {
    return (
      <section className="op-watchlist-panel">
        <h3>Watchlist 품질 관찰</h3>
        <p className="op-empty">watchlist 분석 데이터 없음</p>
      </section>
    );
  }

  const topSymbols = [...analysis.symbols]
    .sort((a, b) => (b.quant.score ?? -1) - (a.quant.score ?? -1))
    .slice(0, 12);

  return (
    <section className="op-watchlist-panel">
      <div className="op-watchlist-head">
        <div>
          <h3>Watchlist 품질 관찰</h3>
          <p>
            Gemini 추천 사유와 정량 점수는 shadow 지표이며 주문에 영향 없음
            {analysis.method?.version ? ` · ${analysis.method.version}` : ''}
          </p>
        </div>
        <div className="op-watchlist-summary">
          <Metric label="후보" value={`${analysis.watchlist_count}`} />
          <Metric label="보유" value={`${analysis.position_count}`} />
          <Metric label="점수화" value={`${analysis.summary.scored_count}`} />
          <Metric label="Top10 후보" value={`${analysis.summary.top_watchlist_overlap}`} />
        </div>
      </div>

      <div className="op-sector-strip">
        {Object.entries(analysis.sector_counts).map(([sector, count]) => (
          <span key={sector}>{sector} {count}</span>
        ))}
      </div>

      <FreshnessStrip analysis={analysis} />
      <OperationsEvidenceSummary summary={analysis.evidence_summary} />

      <div className="op-watchlist-grid">
        {topSymbols.map((item) => (
          <SymbolCard key={item.symbol} item={item} />
        ))}
        {topSymbols.length === 0 && <p className="op-empty">표시할 후보 없음</p>}
      </div>
    </section>
  );
}

function FreshnessStrip({ analysis }: { analysis: WatchlistAnalysis }) {
  const screening = analysis.freshness?.screening || {};
  const status = analysis.freshness?.last_refresh_status || screening;
  const fallback = status.fallback_used ? status.fallback_type || 'fallback' : '정상';
  const promptCount = screening.prompt_symbol_count ?? 0;
  const promptLimit = screening.prompt_symbol_limit ?? 0;
  const usage = analysis.freshness?.usage || {};
  const tokenUsage = usage.last_usage || screening.usage || {};
  const cost = tokenUsage.estimated_cost_usd;

  return (
    <div className="op-freshness-strip">
      <span>RAG {screening.rag_mode || 'unknown'}</span>
      <span>Model {screening.model || tokenUsage.model || '-'}</span>
      <span>뉴스 {screening.news_symbol_count ?? 0}</span>
      <span>Prompt {promptCount}/{promptLimit}</span>
      <span>Fallback {fallback}</span>
      <span>오늘 {usage.total_calls ?? 0}/{usage.total_daily_limit ?? 3}</span>
      <span>자동 {usage.auto_calls ?? 0}/{usage.auto_daily_limit ?? 2}</span>
      <span>다음 {usage.next_slot_et || '-'}</span>
      <span>비용 {cost == null ? '-' : `$${cost}`}</span>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <small>{label}</small>
      <strong>{value}</strong>
    </span>
  );
}

function SymbolCard({ item }: { item: WatchlistSymbol }) {
  const rationale = item.rationale.why_now || item.rationale.catalyst || item.rationale.reason || '추천 사유 없음';
  const score = item.quant.score == null ? '-' : `${item.quant.score}`;
  const recentLoss = item.quant.flags.includes('recent_loss_45d');

  return (
    <article className={`op-watchlist-card ${recentLoss ? 'recent-loss' : ''}`}>
      <div className="op-watchlist-card-top">
        <strong>{item.symbol}</strong>
        <span>{score}</span>
      </div>
      <div className="op-watchlist-card-meta">
        <span>{STATUS_LABEL[item.status]}</span>
        <span>{item.sector || 'Sector 없음'}</span>
        {item.rank && <span>Rank {item.rank}</span>}
      </div>
      <p>{rationale}</p>
      <div className="op-watchlist-card-foot">
        <span>{item.source}</span>
        {item.quant.flags.map((flag) => (
          <span key={flag}>{FLAG_LABEL[flag] || flag}</span>
        ))}
      </div>
    </article>
  );
}
